import { get } from 'lodash';
import { SOURCE_CONTEXT_NAMES, WIDGET_LOGICAL_NAMES } from '@config/Consts';
import BaseService from '@core/common/BaseService';
import { AgentManagerClient, RESOURCES as AGENT_MANAGER_RESOURCES } from '@core/clients/AgentManagerClient';
import { Subscription } from '@core/common/Subscription';
import agentListTransformer from './agentsListTransformer';

const cancellationTokens = {};

function getEligibleResources() {
  return Object.assign({}, {
    AGENT_VIEW: AGENT_MANAGER_RESOURCES.AGENT_VIEW,
    METRICS: AGENT_MANAGER_RESOURCES.METRICS,
  });
}


function createAgentsListClients(params) {
  const contextName = SOURCE_CONTEXT_NAMES[WIDGET_LOGICAL_NAMES.AGENTS];

  const agentViewClient = new AgentManagerClient({
    resource: AGENT_MANAGER_RESOURCES.AGENT_VIEW,
    contextName,
  });
  agentViewClient.useRequestInterceptor(params.interceptor);

  const metricsClient = new AgentManagerClient({
    resource: AGENT_MANAGER_RESOURCES.METRICS,
    contextName,
  });
  metricsClient.useRequestInterceptor(params.interceptor);

  const RESOURCES_TO_CLIENT_MAP = {
    [AGENT_MANAGER_RESOURCES.AGENT_VIEW]: agentViewClient,
    [AGENT_MANAGER_RESOURCES.METRICS]: metricsClient,
  };

  const getClient = resourceName => RESOURCES_TO_CLIENT_MAP[resourceName];

  return {
    getClient,
  };
}

function stopListenToAgentsListClientEvents(resourceName) {
  const client = this.agentsListClients.getClient(resourceName);
  client.clearAllListeners(client.constructor.EVENTS.onPollingSuccess);
  client.clearAllListeners(client.constructor.EVENTS.onPollingError);
}

async function enrichAgentsListData(resourceName, agentsListResponse) {
  const enrichedAgentsListResponse = agentListTransformer.transformResponse(resourceName, {
    agentsListResponse,
  });

  const resourceSubscription = this.subscriptions.find(
    subscriptionRecord => subscriptionRecord.name === resourceName);
  resourceSubscription.subscription.emit(Subscription.EVENTS.onSubscriptionUpdateNotification,
    { name: resourceName, data: enrichedAgentsListResponse });
}

function onAgentsListSubscriptionError(resourceName, error) {
  const resourceSubscription = this.subscriptions.find(
    subscriptionRecord => subscriptionRecord.name === resourceName);
  resourceSubscription.subscription.emit(Subscription.EVENTS.onSubscriptionError, error);
}

function listenToAgentsListClientEvents(settings, resourceName) {
  stopListenToAgentsListClientEvents.call(this, resourceName);
  const client = this.agentsListClients.getClient(resourceName);
  const clientEvents = client.constructor.EVENTS;
  client.on(
    clientEvents.onPollingSuccess,
    enrichAgentsListData.bind(this, resourceName),
    'AgentListServicePollingSuccess');
  client.on(
    clientEvents.onPollingError, onAgentsListSubscriptionError.bind(this, resourceName),
    'AgentListServicePollingError');
}

function agentsListSubscriptionStartMethod(settings) {
  const request = agentListTransformer.transformRequest(settings.name, settings.settings);

  listenToAgentsListClientEvents.call(this, settings.settings, settings.name);
  cancellationTokens[settings.name] = this.agentsListClients.getClient(settings.name)
    .subscribe(request);
}

function agentsListSubscriptionStopMethod(settings) {
  const token = get(cancellationTokens, settings.name);
  if (token) {
    stopListenToAgentsListClientEvents.call(this, settings.name);
    this.agentsListClients.getClient(settings.name).unsubscribe(token);
  }
}

/**
 * Agents List service - retrieves the agentsList data for Agents List widget.
 * Requests data 3 different APIs:
 * Messaging Conversation
 * Messaging CSAT Distribution
 * Search Agent
 */
class AgentsListService extends BaseService {
  constructor() {
    super({ name: 'AgentsListService' });
  }

  async getAgentsListSubscriptions(params) {
    if (!this.subscriptions) {
      this.agentsListClients = createAgentsListClients(params);
      this.subscriptions = [];
      const resources = getEligibleResources();
      Object.keys(resources).forEach((key) => {
        const resourceName = resources[key];
        const resourceSubscription = new Subscription({
          name: `${resourceName}`,
          resourceName,
          handlers: {
            start: agentsListSubscriptionStartMethod.bind(this),
            stop: agentsListSubscriptionStopMethod.bind(this),
          },
        });
        this.subscriptions.push({ name: resourceName, subscription: resourceSubscription });
      });
      return { subscriptions: this.subscriptions };
    }
    return { subscriptions: this.subscriptions };
  }
}

const agentsListService = new AgentsListService();

export default agentsListService;
